import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  BarcodeDetector as BarcodeDetectorPonyfill,
  prepareZXingModule,
  ZXING_WASM_SHA256,
  ZXING_WASM_VERSION,
} from 'barcode-detector/pure'
import {
  ScannerOverlay,
  ScanTop,
  Reticle,
  ScanHint,
  ScanLoading,
  ScanManual,
  ScanError,
  ScanFeedback,
} from './styles'

export interface ScanFeedbackMessage {
  text: string
  tone: 'ok' | 'warn' | 'error'
}

interface Props {
  subtitle?: string
  title?: string
  hint?: ReactNode
  manualLabel?: string
  /**
   * Continua lendo depois de um código, para marcar várias peças em
   * sequência. Cada código lido fica ignorado por REPEAT_MS — por código, não
   * só o último: etiqueta com serial e patrimônio lado a lado alterna os dois.
   */
  continuous?: boolean
  feedback?: ScanFeedbackMessage | null
  onScan: (code: string) => void
  onClose: () => void
  onManual: () => void
}

const REPEAT_MS = 2500

// Mensagem específica por tipo de falha do getUserMedia.
function cameraErrorMessage(e: any): string {
  const name = e?.name || ''
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Permissão de câmera negada. Habilite a câmera para o app nas configurações do celular e tente novamente.'
  }
  if (
    name === 'NotFoundError' ||
    name === 'OverconstrainedError' ||
    name === 'DevicesNotFoundError'
  ) {
    return 'Nenhuma câmera encontrada neste dispositivo.'
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'A câmera está sendo usada por outro aplicativo. Feche-o e tente novamente.'
  }
  return 'Não foi possível acessar a câmera. Verifique a permissão do app/navegador e tente novamente.'
}

// Simbologias aceitas (nomes da spec BarcodeDetector, snake_case). O backend
// valida o texto decodificado contra `bilhetes.Chave` — o tipo do código é
// indiferente. Ingressos atuais usam QR e Code128 (confirmado em teste); o
// leque amplo de 1D cobre variações. Filtramos por getSupportedFormats().
const SCAN_FORMATS = [
  'qr_code',
  'pdf417',
  'itf',
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'code_93',
  'codabar',
]

// iOS (Safari/WebView) não tem BarcodeDetector nativo → usamos o ponyfill WASM
// (motor ZXing-C++), muito mais rápido/confiável que decodificar em JS puro.
//
// O binário é buscado do próprio app (public/zxing_reader.wasm — copiado de
// node_modules/zxing-wasm/dist/reader; manter em sincronia ao atualizar a lib)
// e o SHA-256 é conferido contra a constante oficial da lib: o hosting de prod
// (frontend-builder/Lambda) corrompe binários (transcodifica como UTF-8), então
// um wasm inválido é detectado aqui e baixado do jsdelivr (pinado na versão
// exata) como contingência. Os bytes vão direto ao motor via `wasmBinary`.
let wasmReady: Promise<'local' | 'cdn'> | null = null

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function ensureWasm(): Promise<'local' | 'cdn'> {
  if (!wasmReady) {
    wasmReady = (async () => {
      let source: 'local' | 'cdn' = 'local'
      let bytes: ArrayBuffer | null = null
      try {
        const res = await fetch(
          `${process.env.PUBLIC_URL ?? ''}/zxing_reader.wasm`
        )
        if (res.ok) {
          const buf = await res.arrayBuffer()
          if ((await sha256Hex(buf)) === ZXING_WASM_SHA256) bytes = buf
        }
      } catch {
        // segue para o fallback
      }
      if (!bytes) {
        source = 'cdn'
        const res = await fetch(
          `https://fastly.jsdelivr.net/npm/zxing-wasm@${ZXING_WASM_VERSION}/dist/reader/zxing_reader.wasm`
        )
        if (!res.ok) throw new Error('wasm indisponível (local e CDN)')
        bytes = await res.arrayBuffer()
      }
      prepareZXingModule({ overrides: { wasmBinary: bytes } })
      return source
    })()
    // permite nova tentativa num próximo mount se ambas as origens falharem
    wasmReady.catch(() => {
      wasmReady = null
    })
  }
  return wasmReady
}

// Detector: nativo quando o navegador tem (Chrome desktop/Android — motor do
// próprio browser), senão o ponyfill WASM (iOS). Mesma interface nos dois.
// `?wasm=1` força o ponyfill — permite testar o caminho do iOS num desktop.
async function buildDetector(): Promise<{
  detector: any
  native: boolean
  wasmSource?: 'local' | 'cdn'
}> {
  const forceWasm = window.location.search.includes('wasm=1')
  const Native = forceWasm ? undefined : (window as any).BarcodeDetector
  if (Native?.getSupportedFormats) {
    const supported: string[] = await Native.getSupportedFormats()
    const formats = SCAN_FORMATS.filter((f) => supported.includes(f))
    if (formats.length) return { detector: new Native({ formats }), native: true }
  }
  const wasmSource = await ensureWasm()
  const supported: readonly string[] =
    await BarcodeDetectorPonyfill.getSupportedFormats()
  // cast: SCAN_FORMATS é subconjunto do union de formatos aceito pelo ponyfill
  const formats = SCAN_FORMATS.filter((f) => supported.includes(f)) as any[]
  return {
    detector: new BarcodeDetectorPonyfill({ formats }),
    native: false,
    wasmSource,
  }
}

export function CameraScanner({
  subtitle,
  title = 'Ler ingresso',
  hint,
  manualLabel,
  continuous = false,
  feedback = null,
  onScan,
  onClose,
  onManual,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan
  const continuousRef = useRef(continuous)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(true)
  // Diagnóstico on-screen (útil no iPhone, sem DevTools): ative com ?debug=1
  const debugEnabled = window.location.search.includes('debug=1')
  const [debugInfo, setDebugInfo] = useState<string>('')

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let stopped = false
    let locked = false

    // WebView sem a API de mídia exposta = câmera não liberada pelo app nativo.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        'O acesso à câmera não está disponível neste app. É preciso habilitar a permissão de câmera no aplicativo. Use a busca manual enquanto isso.'
      )
      return
    }

    let stream: MediaStream | null = null
    let timerId = 0

    const seen = new Map<string, number>()
    const handle = (text: string) => {
      if (locked) return
      if (continuousRef.current) {
        const now = Date.now()
        const at = seen.get(text)
        if (at !== undefined && now - at < REPEAT_MS) return
        seen.forEach((t, code) => { if (now - t >= REPEAT_MS) seen.delete(code) })
        seen.set(text, now)
        if (navigator.vibrate) navigator.vibrate(40)
        onScanRef.current(text)
        return
      }
      locked = true
      onScanRef.current(text)
    }

    // Controlamos o stream manualmente (getUserMedia + play) em vez de deixar a
    // lib anexá-lo. Motivo: no React o atributo `muted` do JSX não seta a
    // property no DOM, então a política de autoplay bloqueia o play() e o vídeo
    // fica pausado (tela preta) mesmo com a câmera ligada. Setando muted por JS
    // e dando play() nós mesmos, garantimos o preview.
    const start = async () => {
      // Resolução alta: a decodificação usa a resolução NATIVA do stream, não o
      // tamanho exibido. Código de barras 1D e PDF417 precisam de definição fina
      // nas barras — 640x480 (padrão) não lê.
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      if (stopped) return

      // Android multi-câmera: 'environment' pode abrir a ultra-wide/macro, que
      // tem FOCO FIXO (borrada de perto — não lê nunca). Com a permissão já
      // concedida os labels ficam visíveis; se houver uma traseira "principal"
      // (sem wide/ultra/macro/tele/depth no nome) e não for a atual, trocamos.
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const back = devices.filter(
          (d) => d.kind === 'videoinput' && /back|rear|traseira/i.test(d.label)
        )
        const main = back.find(
          (d) => !/wide|ultra|macro|tele|depth|zoom/i.test(d.label)
        )
        const currentId = stream.getVideoTracks()[0]?.getSettings?.().deviceId
        if (main && main.deviceId && main.deviceId !== currentId) {
          const better = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: main.deviceId },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            audio: false,
          })
          if (stopped) {
            better.getTracks().forEach((t) => t.stop())
            return
          }
          stream.getTracks().forEach((t) => t.stop())
          stream = better
        }
      } catch {
        // heurística falhou — mantém o stream original
      }

      // Foco contínuo precisa ir por applyConstraints (no getUserMedia muitos
      // Androids ignoram). Zoom ~2x quando disponível: o operador afasta o
      // celular para a distância onde a lente principal FOCA — recomendação do
      // próprio ML Kit para leitura de barcode.
      const track = stream.getVideoTracks()[0]
      let zoomApplied = ''
      try {
        const caps: any = track.getCapabilities?.() ?? {}
        const advanced: any[] = []
        if (
          Array.isArray(caps.focusMode) &&
          caps.focusMode.includes('continuous')
        ) {
          advanced.push({ focusMode: 'continuous' })
        }
        if (caps.zoom && caps.zoom.max > 1) {
          const zoom = Math.min(2, caps.zoom.max)
          advanced.push({ zoom })
          zoomApplied = ` · zoom${zoom}x`
        }
        if (advanced.length) await track.applyConstraints({ advanced })
      } catch {
        // best-effort — sem capabilities, segue como está
      }

      video.srcObject = stream
      video.muted = true
      video.setAttribute('playsinline', 'true')
      await video.play()
      if (stopped) return
      setStarting(false)

      // Nativo (Chrome desktop/Android) ou ponyfill WASM (iOS) — mesmo loop.
      const { detector, native, wasmSource } = await buildDetector()
      if (debugEnabled) {
        const cam = (track?.label ?? '').slice(0, 28)
        setDebugInfo(
          `motor: ${native ? 'nativo' : `wasm(${wasmSource})`} · ${video.videoWidth}x${video.videoHeight}${zoomApplied} · ${cam}`
        )
      }

      // Caminho nativo: detectar só a região central (área da moldura) — menos
      // pixels aceleram bem o ML Kit no Android e ignoram códigos fora da mira.
      // iOS/wasm segue com o frame inteiro (createImageBitmap de <video> tem
      // histórico instável no Safari; lá a leitura já está boa).
      let cropOk = native && typeof createImageBitmap === 'function'
      const detectFrame = async () => {
        if (cropOk && video.videoWidth > 0) {
          const w = video.videoWidth
          const h = video.videoHeight
          const cw = Math.round(w * 0.8)
          const ch = Math.round(h * 0.6)
          try {
            const bmp = await createImageBitmap(
              video,
              Math.round((w - cw) / 2),
              Math.round((h - ch) / 2),
              cw,
              ch
            )
            try {
              return await detector.detect(bmp)
            } finally {
              bmp.close()
            }
          } catch {
            cropOk = false // navegador não recorta <video> — usa o frame cheio
          }
        }
        return detector.detect(video)
      }

      // ~8 tentativas/s: leitura em ~125ms no pior caso, sem fritar a CPU do
      // celular (detect a cada frame deixava o aparelho lento e esquentando).
      const tick = async () => {
        if (stopped || locked) return
        try {
          const codes = await detectFrame()
          if (codes && codes.length) {
            if (debugEnabled) {
              setDebugInfo((d) => `${d.split(' · lido')[0]} · lido: ${codes[0].format}`)
            }
            handle(codes[0].rawValue)
            if (locked) return
          }
        } catch {
          // frame sem código / detect falhou nesse frame — segue tentando
        }
        if (!stopped && !locked) timerId = window.setTimeout(tick, 125)
      }
      timerId = window.setTimeout(tick, 125)
    }

    start().catch((e) => {
      if (!stopped) setError(cameraErrorMessage(e))
    })

    return () => {
      stopped = true
      if (timerId) window.clearTimeout(timerId)
      if (stream) stream.getTracks().forEach((t) => t.stop())
      video.srcObject = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ScannerOverlay>
      <video ref={videoRef} playsInline muted />

      <ScanTop>
        <button className="x" aria-label="Fechar" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="t">
          {title}
          {subtitle && <small>{subtitle}</small>}
        </div>
      </ScanTop>

      {!error && starting && (
        <ScanLoading>
          <div className="spinner" />
          Iniciando câmera…
        </ScanLoading>
      )}

      {!error && !starting && (
        <>
          <Reticle>
            <div className="corner tl" />
            <div className="corner tr" />
            <div className="corner bl" />
            <div className="corner br" />
            <div className="line" />
          </Reticle>

          {feedback ? (
            <ScanFeedback $tone={feedback.tone} role="status">{feedback.text}</ScanFeedback>
          ) : (
            <ScanHint>
              {hint ?? (
                <>
                  Posicione o QR Code ou o código de barras dentro da moldura.
                  <br />A leitura acontece automaticamente.
                </>
              )}
            </ScanHint>
          )}
        </>
      )}

      {debugEnabled && debugInfo && (
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            right: 8,
            textAlign: 'center',
            color: '#0f0',
            font: '12px/1.4 monospace',
            textShadow: '0 1px 2px #000',
            pointerEvents: 'none',
          }}
        >
          {debugInfo}
        </div>
      )}

      {!error && (
        <ScanManual>
          <button onClick={onManual}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            {manualLabel ?? 'Digitar código manualmente'}
          </button>
        </ScanManual>
      )}

      {error && (
        <ScanError>
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#f0665f" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5M12 16v.01" />
          </svg>
          <div>{error}</div>
          <button
            onClick={onManual}
            style={{
              background: 'rgba(255,255,255,.14)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 18px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {manualLabel ?? 'Pesquisar manualmente'}
          </button>
        </ScanError>
      )}
    </ScannerOverlay>
  )
}
